import { useEffect, useState } from "react";
import { toast } from "sonner";
import { adminGetGaSettings, adminSaveGaSettings, adminGaOverview } from "@/lib/api";
import { Activity, Loader2, RefreshCw, Save, Settings2, Globe, Users, Eye, MousePointerClick, CheckCircle2, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";

const num = (v) => { const n = Number(v || 0); return n.toLocaleString(); };

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-line bg-surface p-5" data-testid={`ga-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center gap-2 text-ink-3"><Icon className={`h-4 w-4 ${accent || "text-saffron"}`} /><span className="text-xs uppercase tracking-wider">{label}</span></div>
      <p className="mt-2 font-serif-latin text-3xl text-ink">{value}</p>
    </div>
  );
}

function MiniTrend({ rows }) {
  if (!rows?.length) return null;
  const vals = rows.map((r) => Number(r.activeUsers || 0));
  const max = Math.max(...vals, 1);
  return (
    <div className="border border-line bg-surface p-5" data-testid="ga-trend">
      <p className="mb-3 overline-label text-ink-3">Active users · last 30 days</p>
      <div className="flex h-28 items-end gap-[3px]">
        {rows.map((r, i) => (
          <div key={i} title={`${r.date}: ${num(r.activeUsers)}`} className="flex-1 bg-[var(--saffron)] transition-all hover:opacity-80"
            style={{ height: `${Math.max(4, (Number(r.activeUsers || 0) / max) * 100)}%`, opacity: 0.35 + 0.5 * (Number(r.activeUsers || 0) / max) }} />
        ))}
      </div>
    </div>
  );
}

export default function AdminTraffic() {
  const [settings, setSettings] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [showSetup, setShowSetup] = useState(false);
  const [propertyId, setPropertyId] = useState("");
  const [saJson, setSaJson] = useState("");
  const [saving, setSaving] = useState(false);
  const [refreshing, setRefreshing] = useState(false);

  const loadOverview = async (refresh) => {
    setErr("");
    if (refresh) setRefreshing(true); else setLoading(true);
    try {
      const d = await adminGaOverview(refresh);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not load analytics.");
      setData(null);
    } finally { setLoading(false); setRefreshing(false); }
  };

  useEffect(() => {
    adminGetGaSettings().then((s) => {
      setSettings(s);
      setPropertyId(s.property_id || "");
      if (s.configured) loadOverview(false);
      else { setShowSetup(true); setLoading(false); }
    }).catch(() => { setLoading(false); setShowSetup(true); });
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { property_id: propertyId.trim() };
      if (saJson.trim()) payload.service_account_json = saJson.trim();
      const s = await adminSaveGaSettings(payload);
      setSettings(s);
      setSaJson("");
      toast.success("Google Analytics settings saved");
      if (s.configured) { setShowSetup(false); loadOverview(true); }
    } catch (e) {
      toast.error("Could not save", { description: e?.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const configured = settings?.configured;

  return (
    <div data-testid="admin-traffic">
      <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="inline-flex items-center gap-2 font-serif-latin text-3xl text-ink"><Activity className="h-6 w-6 text-saffron" /> Site Analytics</h1>
          <p className="mt-1 max-w-2xl text-sm text-ink-3">Live traffic from <span className="text-ink-2">Google Analytics (GA4)</span> — visitors, sessions, page-views, top pages and countries. {data?.as_of && <>Data as of <span className="text-ink-2">{data.as_of}</span>.</>}</p>
        </div>
        <div className="flex items-center gap-2">
          {configured && <button onClick={() => loadOverview(true)} disabled={refreshing} data-testid="ga-refresh" className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-ink hover:text-ink disabled:opacity-60">{refreshing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />} Refresh</button>}
          <button onClick={() => setShowSetup((v) => !v)} data-testid="ga-setup-toggle" className="inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 transition-colors hover:border-saffron hover:text-saffron"><Settings2 className="h-4 w-4" /> {configured ? "Connection" : "Connect"} {showSetup ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</button>
        </div>
      </div>

      {/* Setup / connection panel */}
      {showSetup && (
        <div className="mb-8 border border-line bg-surface p-5" data-testid="ga-setup">
          <div className="mb-3 flex items-center gap-2">
            {configured ? <span className="inline-flex items-center gap-1.5 text-sm text-green-700"><CheckCircle2 className="h-4 w-4" /> Connected{settings?.property_id ? ` · property ${settings.property_id}` : ""}</span>
              : <span className="text-sm text-ink-2">Connect your GA4 property to see traffic here.</span>}
          </div>
          <ol className="mb-4 list-decimal space-y-1.5 pl-5 text-xs text-ink-3">
            <li>In Google Cloud, create a <span className="text-ink-2">service account</span> and enable the <span className="text-ink-2">Google Analytics Data API</span>. Download its <span className="text-ink-2">JSON key</span>.</li>
            <li>In GA4 → Admin → <span className="text-ink-2">Property Access Management</span>, add the service-account email{settings?.client_email ? <> (<span className="text-ink-2">{settings.client_email}</span>)</> : ""} as a <span className="text-ink-2">Viewer</span>.</li>
            <li>Find your numeric <span className="text-ink-2">Property ID</span> in GA4 → Admin → Property details (a number like 123456789 — not the G-XXXX measurement ID).</li>
          </ol>
          <div className="grid gap-4 md:grid-cols-2">
            <label className="block">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">GA4 Property ID</span>
              <input value={propertyId} onChange={(e) => setPropertyId(e.target.value)} data-testid="ga-property-id" placeholder="123456789" className="w-full border border-line bg-parchment px-3 py-2 text-sm text-ink focus:border-saffron focus:outline-none" />
            </label>
            <div className="md:row-span-2">
              <span className="mb-1 block text-[11px] uppercase tracking-wider text-ink-3">Service-account JSON key {configured && <span className="normal-case text-ink-3">(leave blank to keep the current key)</span>}</span>
              <textarea value={saJson} onChange={(e) => setSaJson(e.target.value)} data-testid="ga-sa-json" rows={7} placeholder='{ "type": "service_account", "project_id": "...", "private_key": "-----BEGIN PRIVATE KEY-----\n...", "client_email": "...@...iam.gserviceaccount.com", ... }' className="w-full border border-line bg-parchment px-3 py-2 font-mono text-xs text-ink focus:border-saffron focus:outline-none" />
            </div>
            <div className="flex items-end">
              <button onClick={save} disabled={saving} data-testid="ga-save" className="inline-flex items-center gap-1.5 bg-[var(--saffron)] px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-[var(--saffron-dark)] disabled:opacity-60">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save &amp; connect</button>
            </div>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-16 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> Loading analytics…</div>
      ) : !configured ? (
        <div className="border border-dashed border-line bg-surface p-10 text-center text-ink-3" data-testid="ga-empty">
          <Globe className="mx-auto mb-3 h-8 w-8 text-gold" />
          <p className="text-ink-2">Google Analytics isn't connected yet.</p>
          <p className="mt-1 text-sm">Open <span className="text-ink-2">Connect</span> above and add your GA4 Property ID + service-account key.</p>
        </div>
      ) : err ? (
        <div className="border border-line bg-surface p-8 text-center" data-testid="ga-error">
          <p className="text-red-700">{err}</p>
          <button onClick={() => loadOverview(true)} className="mt-3 inline-flex items-center gap-1.5 border border-line px-3 py-2 text-sm text-ink-2 hover:border-ink hover:text-ink"><RefreshCw className="h-4 w-4" /> Try again</button>
        </div>
      ) : data ? (
        <div className="space-y-8" data-testid="ga-dashboard">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Stat icon={Activity} label="Active now" value={num(data.realtime_active)} accent="text-green-600" />
            <Stat icon={Users} label="Users · 7 days" value={num(data.last_7_days?.activeUsers)} />
            <Stat icon={MousePointerClick} label="Sessions · 7 days" value={num(data.last_7_days?.sessions)} />
            <Stat icon={Eye} label="Page views · 7 days" value={num(data.last_7_days?.screenPageViews)} />
          </div>

          <MiniTrend rows={data.daily_trend} />

          <div className="grid gap-6 lg:grid-cols-2">
            <div className="border border-line bg-surface">
              <p className="border-b border-line px-5 py-3 overline-label text-ink-3">Top pages · 30 days</p>
              <div className="divide-y divide-line" data-testid="ga-top-pages">
                {(data.top_pages || []).map((p, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm">
                    <span className="min-w-0 truncate text-ink-2">{p.pagePath}</span>
                    <span className="shrink-0 font-serif-latin text-ink">{num(p.screenPageViews)}</span>
                  </div>
                ))}
                {!(data.top_pages || []).length && <p className="px-5 py-4 text-sm text-ink-3">No data yet.</p>}
              </div>
            </div>
            <div className="border border-line bg-surface">
              <p className="border-b border-line px-5 py-3 overline-label text-ink-3">Top countries · 30 days</p>
              <div className="divide-y divide-line" data-testid="ga-top-countries">
                {(data.top_countries || []).map((c, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 px-5 py-2.5 text-sm">
                    <span className="min-w-0 truncate text-ink-2">{c.country || "—"}</span>
                    <span className="shrink-0 font-serif-latin text-ink">{num(c.activeUsers)}</span>
                  </div>
                ))}
                {!(data.top_countries || []).length && <p className="px-5 py-4 text-sm text-ink-3">No data yet.</p>}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3 border border-line bg-surface px-5 py-3 text-sm text-ink-3">
            <span>Users · last 30 days: <span className="font-serif-latin text-ink">{num(data.last_30_days?.activeUsers)}</span></span>
            <a href="https://analytics.google.com/" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-saffron hover:underline">Open full Google Analytics <ExternalLink className="h-3.5 w-3.5" /></a>
          </div>
        </div>
      ) : null}
    </div>
  );
}
